import React, { useState, useEffect, useContext, useMemo } from "react";

import { UserContext } from "../../UserContext";
import { useDispatch, useSelector } from "react-redux";
import { fetchStudents } from "../../store/studentsSlice";
import { openPopup } from "../Common/popupUIStore";

import StudentItem from "../Common/StudentItem";
import { filterStudentsByBookingStatus } from "../../hooks/useBookingStatusMonitor";
import useAutoLoadMore from "../../hooks/useAutoLoadMore";
import {
   bookingStatusLabel,
   bookingStatusShortLabel,
   bookingStatusTitle,
   bookingStatusTone,
} from "../../utils/bookingStatus";

const PAGE_SIZE = 16;
const PAGE_SIZE_MOBILE = 4;
const MOBILE_BP = 768;

/* ===================== Helpers ===================== */
function useIsMobile(bp = MOBILE_BP) {
   const [isMobile, setIsMobile] = useState(() => {
      if (typeof window === "undefined") return false;
      return window.matchMedia(`(max-width:${bp}px)`).matches;
   });

   useEffect(() => {
      if (typeof window === "undefined") return;

      const mq = window.matchMedia(`(max-width:${bp}px)`);
      const onChange = (e) => setIsMobile(e.matches);

      if (mq.addEventListener) mq.addEventListener("change", onChange);
      else mq.addListener(onChange);

      return () => {
         if (mq.removeEventListener) mq.removeEventListener("change", onChange);
         else mq.removeListener(onChange);
      };
   }, [bp]);

   return isMobile;
}

// fără starea programării (alt rol sau încă nu a venit de la server)
const NO_MONITOR = { enabled: false, byUser: new Map(), stateOf: () => null };

/* ===================== Component ===================== */
// Căutarea, filtrul după stare și starea programării vin din bara de sus a
// paginii (AdminHome); onCount primește câți studenți se văd după ele.
// Avatarul (poza sau inițialele, culoarea după nume) îl face StudentItem (AvatarUI).
function StudentsManager({
   query: rawQuery = "",
   statusFilter = null,
   bookingMonitor = NO_MONITOR,
   onCount,
}) {
   const { user } = useContext(UserContext);
   const dispatch = useDispatch();
   const {
      list: students = [],
      loading,
      error,
   } = useSelector((state) => state.students);

   const isMobile = useIsMobile(MOBILE_BP);
   const pageSize = isMobile ? PAGE_SIZE_MOBILE : PAGE_SIZE;

   const [visibleCount, setVisibleCount] = useState(pageSize);

   useEffect(() => {
      if (user?.role === "ADMIN") dispatch(fetchStudents());
   }, [dispatch, user]);

   // Starea programării (galben / verde / roșu) — doar pentru admin și manager.
   const bookingStateOf = bookingMonitor.stateOf;

   const handleOpenStudentPopup = (student) => {
      openPopup("studentDetails", { student });
   };

   const query = useMemo(
      () => String(rawQuery || "").trim().toLowerCase(),
      [rawQuery],
   );

   const studentsOnly = useMemo(
      () => (students || []).filter((s) => s.role === "USER"),
      [students],
   );

   const filteredStudents = useMemo(() => {
      const byStatus = filterStudentsByBookingStatus(
         studentsOnly,
         bookingMonitor.byUser,
         statusFilter,
      );
      if (!query) return byStatus;

      return byStatus.filter((s) =>
         `${s.firstName} ${s.lastName} ${s.email} ${s.phone || ""}`
            .toLowerCase()
            .includes(query),
      );
   }, [studentsOnly, query, statusFilter, bookingMonitor]);

   useEffect(() => {
      setVisibleCount(pageSize);
   }, [query, students, pageSize, statusFilter]);

   const visibleStudents = useMemo(
      () => filteredStudents.slice(0, visibleCount),
      [filteredStudents, visibleCount],
   );


   const totalResults = filteredStudents.length;
   useEffect(() => {
      onCount?.(totalResults);
   }, [onCount, totalResults]);
   const canLoadMore = !loading && !error && visibleCount < totalResults;

   // fără „Afișează încă”: următorii vin singuri aproape de capătul listei (jos, sau în
   // dreapta pe telefon, unde rândul se derulează orizontal)
   const autoLoad = useAutoLoadMore(canLoadMore, () =>
      setVisibleCount((c) => Math.max(c, Math.min(visibleCount + pageSize, totalResults))),
   );

   return (
      <div className="studentsUI">
         {/* Header */}
         <div className="studentsUI__header">
            <h2 className="studentsUI__title">Studenți</h2>
         </div>

         {/* Grid */}
         <div className="studentsUI__gridWrap">
            <div className="studentsUI__grid" ref={autoLoad.ref} onScroll={autoLoad.onScroll}>
               {loading && (
                  <p style={{ gridColumn: "1 / -1" }}>
                     Se încarcă studenții...
                  </p>
               )}
               {error && (
                  <p style={{ gridColumn: "1 / -1", color: "red" }}>{error}</p>
               )}

               {!loading &&
                  !error &&
                  visibleStudents.map((student, idx) => {
                     const key = String(
                        student.id ??
                           student.email ??
                           student.phone ??
                           `__idx_${idx}`,
                     );
                     const bookingState = bookingStateOf(student);
                     const tone = bookingStatusTone(bookingState);

                     return (
                        <StudentItem
                           key={student.id ?? key}
                           student={student}
                           onOpen={handleOpenStudentPopup}
                           highlightQuery={rawQuery}
                           highlightClassName="studentItem__highlight"
                           phoneCopy
                           statusText={bookingStatusShortLabel(bookingState)}
                           statusTone={tone}
                           statusTitle={
                              bookingState
                                 ? `${bookingStatusLabel(bookingState)} — ${bookingStatusTitle(bookingState)}`
                                 : ""
                           }
                        />
                     );
                  })}

               {/* Empty */}
               {!loading && !error && totalResults === 0 && (
                  <p className="studentsUI__empty">
                     {statusFilter
                        ? "Niciun student cu această stare."
                        : "Nu s-au găsit studenți."}
                  </p>
               )}
            </div>
         </div>
      </div>
   );
}

export default StudentsManager;
